import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_HREF } from '@nrx/booking-ui';
import { Box, Button, Card, Container, Heading, Link, Text } from '@nrx/ui';
import type { JSX } from 'react';

type ConsultExchangeFailedProps = {
  /**
   * `Link Expired` (invalid/expired token), `Temporarily unavailable` (exchange 503), or
   * `Connection problem` (the request never reached the server).
   */
  title: string;
  message: string | null;
  /** Re-runs the exchange. Omitted when retrying could only re-fail — see `exchangeFailRetryable`. */
  onRetry?: (() => void) | undefined;
};

/**
 * Terminal state when the magic-link exchange fails; only the expired case offers contact details.
 *
 * @remarks The two contact anchors are `Link underline` wrapping a `Text`, not `.nrx-link`'s own
 * colour: at body size the brand blue measures 3.25:1 on these surfaces and fails AA (filed
 * upstream as issue #1058). The always-on underline keeps colour from being the sole link signal
 * (W4, WCAG 1.4.1) — the same pattern the shell footer uses.
 *
 * The title is the only heading on this route (`ConsultLayout` deliberately adds none), so it is
 * `h1`. It was `h2` before this restyle, which left the page with no top-level heading;
 * `app.spec.tsx` pins it as a heading by name, not by level.
 */
export function ConsultExchangeFailed({
  title,
  message,
  onRetry,
}: ConsultExchangeFailedProps): JSX.Element {
  return (
    <Container as="section" size="4" pt="8" pb="10">
      <Card elevation="soft" className="consult-panel consult-panel--narrow">
        <Heading as="h1" size="7" className="consult-panel__title">
          {title}
        </Heading>
        <Text as="p" color="muted">
          {message}
        </Text>
        {onRetry ? (
          <Box mt="4">
            <Button tone="primary" onClick={onRetry}>
              Try again
            </Button>
          </Box>
        ) : null}
        {title === 'Link Expired' && (
          <Text as="p" color="muted" className="consult-panel__contact">
            Please call us to discuss your next consult booking at{' '}
            <Link href={SUPPORT_PHONE_HREF} underline>
              <Text weight="medium">{SUPPORT_PHONE_DISPLAY}</Text>
            </Link>{' '}
            or email us at{' '}
            <Link href="mailto:food@nourishedrx.com" underline>
              <Text weight="medium">food@nourishedrx.com</Text>
            </Link>
            .
          </Text>
        )}
      </Card>
    </Container>
  );
}
